import { ArrowLeft, CheckCircle2 } from "lucide-react";
import { Navigate, Link, useLocation, useNavigate } from "react-router-dom";
import { Logo } from "@/components/common/logo";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/features/auth/use-auth";

interface LocationState {
  from?: { pathname: string };
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="size-5" aria-hidden="true">
      <path fill="#4285F4" d="M21.6 12.227c0-.709-.064-1.391-.182-2.045H12v3.868h5.382a4.6 4.6 0 0 1-1.996 3.018v2.51h3.232c1.89-1.74 2.982-4.305 2.982-7.35Z" />
      <path fill="#34A853" d="M12 22c2.7 0 4.964-.896 6.618-2.423l-3.232-2.509c-.896.6-2.041.955-3.386.955-2.605 0-4.81-1.76-5.6-4.123H3.06v2.59A9.997 9.997 0 0 0 12 22Z" />
      <path fill="#FBBC05" d="M6.4 13.9A6.013 6.013 0 0 1 6.086 12c0-.659.114-1.3.314-1.9V7.51H3.06A9.997 9.997 0 0 0 2 12c0 1.614.386 3.141 1.06 4.49L6.4 13.9Z" />
      <path fill="#EA4335" d="M12 5.977c1.468 0 2.786.505 3.823 1.496l2.868-2.868C16.96 2.99 14.695 2 12 2a9.997 9.997 0 0 0-8.94 5.51L6.4 10.1c.79-2.364 2.995-4.123 5.6-4.123Z" />
    </svg>
  );
}

export function LoginPage() {
  const { user, loading, error, signInWithGoogle } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as LocationState | null;

  if (!loading && user) {
    return <Navigate to={state?.from?.pathname ?? "/app"} replace />;
  }

  const handleSignIn = async () => {
    try {
      await signInWithGoogle();
      void navigate(state?.from?.pathname ?? "/app", { replace: true });
    } catch {
      // The provider exposes a safe, human-readable error below.
    }
  };

  return (
    <main className="grid min-h-screen bg-slate-50 lg:grid-cols-2">
      <section className="flex flex-col p-6 sm:p-10">
        <div className="flex items-center justify-between">
          <Logo />
          <Button asChild variant="ghost" size="sm">
            <Link to="/">
              <ArrowLeft className="size-4" /> Back
            </Link>
          </Button>
        </div>
        <div className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center py-16">
          <h1 className="text-4xl font-bold tracking-tight text-slate-950">
            Welcome to Master Notes
          </h1>
          <p className="mt-3 leading-7 text-slate-600">
            Sign in to organize your ideas and collaborate with the people who
            move them forward.
          </p>

          {error && (
            <div
              role="alert"
              className="mt-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
            >
              {error}
            </div>
          )}

          <Button
            size="lg"
            variant="outline"
            className="mt-7 w-full bg-white"
            onClick={() => void handleSignIn()}
            disabled={loading}
          >
            <GoogleIcon />
            Continue with Google
          </Button>
          <p className="mt-5 text-center text-xs leading-5 text-slate-400">
            By continuing, you agree to use Master Notes responsibly. We only
            request the Google profile details needed to create your account.
          </p>
        </div>
      </section>

      <section className="relative hidden overflow-hidden bg-slate-950 p-12 text-white lg:flex lg:flex-col lg:justify-between">
        <div className="absolute -right-24 -top-24 size-96 rounded-full bg-indigo-500/20 blur-3xl" />
        <p className="relative text-sm font-semibold text-indigo-300">
          ORGANIZE KNOWLEDGE. COLLABORATE CLEARLY.
        </p>
        <div className="relative max-w-lg">
          <p className="text-4xl font-semibold leading-tight tracking-tight">
            “A calm workspace makes ambitious work feel possible.”
          </p>
          <div className="mt-10 space-y-4">
            {[
              "Structured pages that stay easy to navigate",
              "Clear roles for every collaborator",
              "Private-by-default access controls",
            ].map((item) => (
              <div key={item} className="flex items-center gap-3 text-slate-300">
                <CheckCircle2 className="size-5 text-indigo-400" />
                {item}
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
